const { useEffect, useState } = React;

function versionLabel(version, lang) {
  const date = new Date(version.captured_at).toLocaleString(lang === 'he' ? 'he-IL' : 'en-GB', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const deleted = version.deleted ? (lang === 'he' ? ' · נמחק' : ' · deleted') : '';
  return `v${version.version_no} · ${date}${deleted}`;
}

function DiffTable({ opcodes, lang }) {
  const he = lang === 'he';
  let oldLine = 0;
  let newLine = 0;
  const rows = opcodes.flatMap(op => {
    if (op.tag === 'equal') {
      return op.old.map(text => ({ kind: 'equal', old: ++oldLine, next: ++newLine, text }));
    }
    return [
      ...op.old.map(text => ({ kind: 'delete', old: ++oldLine, next: null, text })),
      ...op.new.map(text => ({ kind: 'insert', old: null, next: ++newLine, text })),
    ];
  });
  return (
    <div className="trig-diff-scroll" tabIndex={0} role="region" aria-label={he ? 'הבדלי קוד' : 'Code differences'} dir="ltr">
      <table className="trig-diff">
        <thead><tr>
          <th scope="col">{he ? 'ישן' : 'Old'}</th>
          <th scope="col">{he ? 'חדש' : 'New'}</th>
          <th scope="col">{he ? 'שינוי' : 'Change'}</th>
          <th scope="col">SQL</th>
        </tr></thead>
        <tbody>{rows.map((line, i) => (
          <tr key={i} data-change={line.kind}>
            <td className="trig-line-no">{line.old}</td>
            <td className="trig-line-no">{line.next}</td>
            <td className="trig-line-mark" aria-label={line.kind === 'insert'
              ? (he ? 'נוסף' : 'Added') : line.kind === 'delete'
                ? (he ? 'הוסר' : 'Removed') : (he ? 'ללא שינוי' : 'Unchanged')}>
              {line.kind === 'insert' ? '+' : line.kind === 'delete' ? '−' : ' '}
            </td>
            <td><code>{line.text || '\u00a0'}</code></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export function TriggerHistory({ ename, tr, lang, id }) {
  const he = lang === 'he';
  const unit = [ename, tr.column, tr.name].filter(Boolean).join('/').toUpperCase();
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [historyRetry, setHistoryRetry] = useState(0);
  const [selection, setSelection] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffError, setDiffError] = useState(null);
  const [diffRetry, setDiffRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setHistory(null);
    setHistoryError(null);
    setSelection(null);
    setDiff(null);
    PCMAdapter.getCodeHistory(ename, unit, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      setHistory(result);
      if (result.versions.length) {
        setSelection({ to: result.versions[0].id, from: result.versions[1]?.id ?? null });
      }
    }).catch(error => { if (!controller.signal.aborted) setHistoryError({ status: error.status }); });
    return () => controller.abort();
  }, [ename, unit, historyRetry]);

  useEffect(() => {
    const controller = new AbortController();
    setDiff(null);
    setDiffError(null);
    if (selection) {
      PCMAdapter.getCodeDiff(selection.to, selection.from, controller.signal).then(result => {
        if (!controller.signal.aborted) setDiff(result);
      }).catch(error => {
        if (!controller.signal.aborted) setDiffError({ status: error.status });
      });
    }
    return () => controller.abort();
  }, [selection, diffRetry]);

  const versions = history?.versions || [];
  const selectedTo = versions.find(version => version.id === selection?.to);
  const older = versions.filter(version => version.version_no < (selectedTo?.version_no ?? 0));
  // The oldest of the latest 100 captures can still have a predecessor.
  const olderOptions = diff?.from && !older.some(version => version.id === diff.from.id)
    ? [...older, diff.from] : older;
  const additions = diff?.opcodes.reduce((n, op) => n + (op.tag === 'equal' ? 0 : op.new.length), 0) ?? 0;
  const removals = diff?.opcodes.reduce((n, op) => n + (op.tag === 'equal' ? 0 : op.old.length), 0) ?? 0;
  const retry = action => <button type="button" className="btn btn-sm" onClick={action}>{he ? 'נסה שוב' : 'Retry'}</button>;

  return (
    <section id={id} className="trigger-history" aria-label={he ? 'היסטוריית הטריגר' : 'Trigger history'}>
      <div className="trig-history-title">
        <h4>{he ? 'גרסאות שמורות' : 'Saved versions'}</h4>
        <span className="mono" dir="ltr">{unit}</span>
      </div>
      {historyError ? (
        <div className="trig-history-message" role="alert">
          <span>{historyError.status === 409
            ? (he ? 'יש טריגרים עם אותו שם באותיות גדולות וקטנות. לא ניתן להתאים את ההיסטוריה שלהם בוודאות.'
              : 'Multiple trigger variants share this name. Their saved history cannot be matched reliably.')
            : (he ? 'לא ניתן לטעון את ההיסטוריה.' : 'Could not load history.')}</span>
          {historyError.status !== 409 && retry(() => setHistoryRetry(n => n + 1))}
        </div>
      ) : !history ? (
        <p className="trig-history-message" role="status">{he ? 'טוען גרסאות שמורות…' : 'Loading saved versions…'}</p>
      ) : !versions.length ? (
        <p className="trig-history-message" role="status">{he ? 'עדיין אין גרסאות שמורות לטריגר הזה.' : 'No saved versions for this trigger yet.'}</p>
      ) : (
        <>
          <div className="trig-history-selectors">
            <label>
              <span>{he ? 'גרסה ישנה' : 'Older version'}</span>
              <select aria-label={he ? 'גרסה ישנה' : 'Older version'} value={selection?.from ?? diff?.from?.id ?? ''}
                onChange={event => { setDiff(null); setSelection({ ...selection, from: Number(event.target.value) }); }}
                disabled={!olderOptions.length}>
                {!olderOptions.length && <option value="">{selectedTo?.version_no === 1
                  ? (he ? 'לפני התיעוד הראשון' : 'Before first capture')
                  : (he ? 'הגרסה הקודמת' : 'Previous captured version')}</option>}
                {olderOptions.map(version => <option key={version.id} value={version.id}>{versionLabel(version, lang)}</option>)}
              </select>
            </label>
            <span className="trig-history-arrow" aria-hidden="true">{he ? '←' : '→'}</span>
            <label>
              <span>{he ? 'גרסה חדשה' : 'Newer version'}</span>
              <select aria-label={he ? 'גרסה חדשה' : 'Newer version'} value={selection?.to ?? ''} onChange={event => {
                const index = versions.findIndex(version => version.id === Number(event.target.value));
                setDiff(null);
                setSelection({ to: versions[index].id, from: versions[index + 1]?.id ?? null });
              }}>
                {versions.map(version => <option key={version.id} value={version.id}>{versionLabel(version, lang)}</option>)}
              </select>
            </label>
          </div>
          <p className="trig-history-note">{he
            ? 'הזמנים הם זמני תיעוד לפי השעון המקומי שלך. ייתכן ששינויים בין תיעודים לא נשמרו.'
            : 'Capture times use your local time. Edits between captures may not be recorded.'}</p>
          {history.total > versions.length && <p className="trig-history-note">{he
            ? `מוצגות ${versions.length} הגרסאות האחרונות מתוך ${history.total}.`
            : `Showing the latest ${versions.length} of ${history.total} captures.`}</p>}
          {diffError ? (
            <div className="trig-history-message" role="alert">
              <span>{diffError.status === 413
                ? (he ? 'ההשוואה גדולה מדי לתצוגה כאן. אפשר לקרוא את הקוד הנוכחי למעלה.' : 'This comparison is too large to display. View the current SQL above.')
                : (he ? 'לא ניתן לטעון את ההשוואה.' : 'Could not load comparison.')}</span>
              {diffError.status !== 413 && retry(() => setDiffRetry(n => n + 1))}
            </div>
          ) : !diff ? (
            <p className="trig-history-message" role="status">{he ? 'משווה גרסאות…' : 'Comparing versions…'}</p>
          ) : (
            <>
              <div className="trig-history-summary" role="status">
                {!diff.from && <span>{he ? 'גרסה מתועדת ראשונה · אין גרסה קודמת להשוואה' : 'First captured version · no earlier snapshot to compare'}</span>}
                {diff.to.deleted && <span>{he ? 'הטריגר נמחק בתיעוד הזה.' : 'Trigger deleted in this capture.'}</span>}
                <span className="trig-added">+{additions} {he ? 'נוספו' : 'added'}</span>
                <span className="trig-removed">−{removals} {he ? 'הוסרו' : 'removed'}</span>
                {diff.from && !additions && !removals && <span>{he ? 'אין הבדלי קוד בין הגרסאות האלה.' : 'No code differences between these versions.'}</span>}
              </div>
              <DiffTable opcodes={diff.opcodes} lang={lang}/>
            </>
          )}
        </>
      )}
    </section>
  );
}
